// src/hooks/useUsers.ts
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { mockUsers } from "@/mocks/users";
import type { User, NewUser } from "@/types/user";

export function useUsers(searchQuery?: string) {
    const { t } = useTranslation();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const loadUsers = async () => {
            try {
                setLoading(true);
                // TODO: Implement actual API call
                // const response = await fetch(`/api/users${searchQuery ? `?search=${searchQuery}` : ''}`);
                // const data = await response.json();

                // Use mock data for now
                let filteredUsers = [...mockUsers];
                
                // Apply search filter if search query is provided
                if (searchQuery && searchQuery.length >= 3) {
                    const query = searchQuery.toLowerCase();
                    filteredUsers = filteredUsers.filter(
                        (user) =>
                            (user.firstName?.toLowerCase() || "").includes(query) ||
                            (user.lastName?.toLowerCase() || "").includes(query) ||
                            (user.email?.toLowerCase() || "").includes(query)
                    );
                }
                
                setUsers(filteredUsers);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err : new Error(t("error.users.failedToLoadUsers")));
            } finally {
                setLoading(false);
            }
        };

        loadUsers();
    }, [searchQuery]);

    const addUser = async (user: NewUser): Promise<void> => {
        try {
            // TODO: Implement actual API call
            // const response = await fetch("/api/users", {
            //     method: "POST",
            //     headers: {
            //         "Content-Type": "application/json",
            //     },
            //     body: JSON.stringify(user),
            // });
            // const newUser = await response.json();

            // Mock adding a user
            const newUser = {
                ...user,
                id: String(users.length + 1),
                role: t("user.roles.user"),
                status: t("user.statuses.active"),
                mijnpsAccess: false,
                dataEntryAccess: user.hasDataEntryAccess,
            } as User;
            setUsers((prev) => [...prev, newUser]);
        } catch (err) {
            throw err instanceof Error ? err : new Error(t("error.users.failedToAddUser"));
        }
    };

    const updateUser = async (user: User): Promise<void> => {
        try {
            // TODO: Implement actual API call
            // const response = await fetch(`/api/users/${user.id}`, {
            //     method: "PUT",
            //     headers: {
            //         "Content-Type": "application/json",
            //     },
            //     body: JSON.stringify(user),
            // });
            // const updatedUser = await response.json();

            // Mock updating a user
            setUsers((prev) =>
                prev.map((u) => (u.id === user.id ? user : u))
            );
        } catch (err) {
            throw err instanceof Error ? err : new Error(t("error.users.failedToUpdateUser"));
        }
    };

    const deleteUser = async (id: string): Promise<void> => {
        try {
            // TODO: Implement actual API call
            // await fetch(`/api/users/${id}`, {
            //     method: "DELETE",
            // });
            // setUsers((prev) => prev.filter((u) => u.id !== id));

            // Mock deleting a user
            setUsers((prev) => prev.filter((u) => u.id !== id));
        } catch (err) {
            throw err instanceof Error ? err : new Error(t("error.users.failedToDeleteUser"));
        }
    };

    return {
        users,
        loading,
        error,
        addUser,
        updateUser,
        deleteUser,
    };
} 